import { useEffect } from 'react';
import { useAppStore } from '@store/appStore';
import { imageCache } from '@services/imageCache';
import { getFrameImagePath } from '@services/colmapLoader';

/**
 * Hook to manage image preloading based on current camera position
 * Automatically preloads nearby frames when camera changes
 */
export function useImagePreloader(shopId: string) {
  const cameras = useAppStore((state) => state.cameras);
  const currentCameraIndex = useAppStore((state) => state.currentCameraIndex);
  const currentSectionFramesPath = useAppStore((state) => state.currentSectionFramesPath);
  
  useEffect(() => {
    if (cameras.length === 0 || currentCameraIndex === null || !currentSectionFramesPath) {
      return;
    }
    
    
    const getUrlForIndex = (index: number): string => {
      const camera = cameras[index];
      if (!camera) {
        throw new Error(`Camera at index ${index} not found`);
      }
      const url = getFrameImagePath(shopId, camera.frame_file, currentSectionFramesPath || undefined);
      if (index === currentCameraIndex) {
        console.log(`
┌────────────────────────────────────────────────────────────────┐
│ 🎬 [useImagePreloader] CURRENT FRAME
├────────────────────────────────────────────────────────────────┤
│ Index: ${index}
│ Frame file: ${camera.frame_file}
│ URL: ${url}
└────────────────────────────────────────────────────────────────┘
        `);
      }
      return url;
    };
    
    // Preload images around current position
    imageCache.preloadAround(
      currentCameraIndex,
      cameras.length,
      getUrlForIndex
    ).catch(error => {
      console.error('[useImagePreloader] Error preloading images:', error);
    });
    
  }, [currentCameraIndex, cameras, shopId, currentSectionFramesPath]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't clear cache on unmount - keep for navigation
      // imageCache.clear();
    };
  }, []);
  
  return {
    cacheStats: imageCache.getStats(),
    clearCache: () => imageCache.clear(),
  };
}
